import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// localStorage key for the cached user — only non-sensitive profile data
const AUTH_CACHE_KEY = 'iling_auth_user';

function readCache() {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(user) {
  try {
    if (user) localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(AUTH_CACHE_KEY);
  } catch {}
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage so page reloads don't flash/redirect before /me responds
  const [user, setUser] = useState(() => readCache());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // If we already have a cached user, don't block on loading
  useEffect(() => {
    if (readCache()) setLoading(false);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 15000,
      });
      const userData = response.data;
      setUser(userData);
      writeCache(userData);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      // ONLY clear the session on an explicit 401/403.
      // 520 (Cloudflare origin error), 502/503/504, network timeouts,
      // and 500s are transient — logging the user out on those causes
      // permanent-looking "logged-off" experiences after every hiccup.
      if (status === 401 || status === 403) {
        setUser(null);
        writeCache(null);
      }
      // For transient failures keep the existing user state (from cache or prior check)
      if (status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Strip any stale `#session_id=...` from the URL before redirecting to
    // Emergent Auth. A leftover hash from a previous (failed or completed)
    // login will be appended to our state cookie and trigger Emergent's
    // "Invalid state parameter" rejection on the round trip.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, {
        withCredentials: true
      });
      setUser(response.data);
      writeCache(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name
      }, {
        withCredentials: true
      });
      setUser(response.data);
      writeCache(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      writeCache(null);
    }
  };

  const exchangeSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true
      });
      setUser(response.data);
      writeCache(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    loginWithPassword,
    register,
    logout,
    exchangeSession,
    checkAuth,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
